import type {
  GlobalError,
  InputValidationText,
  ProcessingKey,
} from 'constants/rootConstants'
import type { GrowthBook } from '@growthbook/growthbook'
import firebase from 'firebase/compat/app'
import type { TRemoteConfigVariantData } from 'helpers/getRemoteConfigVariantData'
import type { Stripe } from '@stripe/stripe-js'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { IGeoDataResponse } from './models/geo.model'
import type { ILeadRaw } from './models/lead.model'
import type { TPlansMap } from './models/plan.model'
import type { PageId } from 'constants/pages'

interface IGlobalStore {
  accountEmail: string
  accountEmailErrorMessage: InputValidationText | null
  accountPasswordErrorMessage: InputValidationText | null
  currentPageId: PageId | null
  currentPageIndex: number
  currentPageNumber: number
  firebaseRemoteConfig: firebase.remoteConfig.RemoteConfig | null
  isFirebaseRemoteConfigReady: boolean
  isFrcSettled: boolean
  firebaseRemoteConfigVariantData: TRemoteConfigVariantData | null
  growthbook: GrowthBook | null
  growthbookVariantData: TRemoteConfigVariantData | null
  geoData: IGeoDataResponse | null
  globalError: GlobalError | string | null
  initialCohort: string // used only for the first lead requests, take cohort from lead for common use
  inputEmail: string // used only for email page, after the page - take email from lead
  // abtest_web_127_first_screen group, resolved once before the flow renders.
  // null until resolved; the flow shape must never change within a session.
  firstScreenVariantIntl2Lw: string | null
  isEventLoggerInitialized: boolean
  isProcessing: boolean
  isSessionStartEventSent: boolean
  isSubmitModalOpen: boolean
  isPrepaidCardBlocked: boolean
  isPaymentFlowsShown: boolean
  walletPrefetchStatus: 'idle' | 'pending' | 'ready' | 'unavailable'
  isStripeLoaded: boolean
  hasVisitedCancelOfferIntl2: boolean
  hasVisitedCancelOfferIntl2Lw: boolean
  hasVisitedCancelOfferIntlMw: boolean
  hasVisitedCancelOfferIntl3: boolean
  paymentOpenMethod: 'scrolled' | 'cta_btn_clicked' | null
  paymentBtnLocation: string | null
  paymentBtnName: string | null
  cohortLeadPlans: TPlansMap | null
  cohortLeadCurrency: string
  lead: ILeadRaw | null // don't use steps from lead, use hardcoded client steps config from the cohort
  processingKeys: ProcessingKey[]
  stripePromise: Promise<Stripe | null> | null
  stripeError: string
  stripeErrorCode: string | null
  startProcessing: (key: ProcessingKey) => void
  stopProcessing: (key: ProcessingKey) => void
}

const INITIAL_STORE: IGlobalStore = {
  accountEmail: '',
  accountEmailErrorMessage: null,
  accountPasswordErrorMessage: null,
  currentPageId: null,
  currentPageIndex: -1,
  currentPageNumber: 0,
  firebaseRemoteConfig: null,
  isFirebaseRemoteConfigReady: false,
  isFrcSettled: false,
  firebaseRemoteConfigVariantData: null,
  growthbook: null,
  growthbookVariantData: null,
  geoData: null,
  globalError: null,
  initialCohort: '',
  inputEmail: '',
  firstScreenVariantIntl2Lw: null,
  isEventLoggerInitialized: false,
  isProcessing: false,
  isSessionStartEventSent: false,
  isSubmitModalOpen: false,
  isPrepaidCardBlocked: false,
  isPaymentFlowsShown: false,
  walletPrefetchStatus: 'idle',
  isStripeLoaded: false,
  hasVisitedCancelOfferIntl2: false,
  hasVisitedCancelOfferIntl2Lw: false,
  hasVisitedCancelOfferIntlMw: false,
  hasVisitedCancelOfferIntl3: false,
  paymentOpenMethod: null,
  paymentBtnLocation: null,
  paymentBtnName: null,
  cohortLeadPlans: null,
  cohortLeadCurrency: '',
  lead: null,
  processingKeys: [],
  stripePromise: null,
  stripeError: '',
  stripeErrorCode: null,
  startProcessing: () => undefined,
  stopProcessing: () => undefined,
}

export const useZustandStore = create<IGlobalStore>()(
  devtools((set) => ({
    ...INITIAL_STORE,
    startProcessing: (key: ProcessingKey) =>
      set((state) => ({
        isProcessing: true,
        processingKeys: state.processingKeys.concat(key),
      })),
    stopProcessing: (key: ProcessingKey) =>
      set((state) => {
        const processingKeys = state.processingKeys.filter(
          (processingKey) => processingKey && processingKey !== key,
        )
        return {
          isProcessing: !!processingKeys.length,
          processingKeys,
        }
      }),
  })),
)
export const useZustandGlobalStore = useZustandStore
